import { useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { FlagIcon, IconButton, Popover, Tooltip } from '@buffer-mono/popcorn'

import styles from './ChartAnnotationFlag.module.css'

// Square trigger side length — matches IconButton `size="small"` (--control-size-sm).
const ANNOTATION_CHIP_SIZE = 24
// Gap below the x-axis line so the flag clears the date labels with padding.
const ANNOTATION_CHIP_TOP_GAP = 34
// Larger gap for the stacked comparison axis, which adds a second (italic) date
// line below the primary label the single-line gap doesn't clear.
const ANNOTATION_CHIP_TOP_GAP_STACKED = 48

interface ChartAnnotationFlagProps {
  /** Injected by Recharts when this is used as a `ReferenceLine` label. */
  viewBox?: { x?: number; y?: number; width?: number; height?: number }
  /** Accessible name for the flag button. */
  ariaLabel: string
  /**
   * Popover body shown when the flag is opened. Named `popoverContent` (not
   * `content`) to avoid Recharts' `Label.content` prop, which it injects when
   * cloning the label element and would otherwise clobber this.
   */
  popoverContent: ReactNode
  /**
   * Controlled open state. Owned by the chart (not the Popover) so it survives
   * Recharts re-rendering this label, and so the chart can close the popover when
   * the user moves back onto the graph.
   */
  open: boolean
  /** Fires as the popover opens/closes so the chart can toggle its dotted line. */
  onOpenChange: (open: boolean) => void
  /**
   * Whether the x-axis is showing stacked comparison-period labels (two lines).
   * Drops the flag lower so it clears the taller two-line labels.
   */
  stacked?: boolean
}

/**
 * A flag chip sitting below the date label at the foot of an annotation's
 * reference line. Rendered as a `ReferenceLine` label so Recharts positions it on
 * the time axis; a `foreignObject` lets the flag be real HTML so it can host a
 * Popcorn {@link Popover}. Activating the flag (click, tap, or keyboard) opens the
 * popover and (via `onOpenChange`) the chart's dotted vertical line.
 */
export function ChartAnnotationFlag({
  viewBox,
  ariaLabel,
  popoverContent,
  open,
  onOpenChange,
  stacked = false,
}: ChartAnnotationFlagProps): JSX.Element | null {
  // Drive the tooltip from hover only, not focus: the popover returns focus to
  // this flag on close (correct for keyboard), and a focus-opened tooltip would
  // then flash on a flag the pointer isn't over. `!open` also stops it overlapping
  // the popover.
  const [hovered, setHovered] = useState(false)

  if (viewBox?.x == null) return null
  const x = viewBox.x
  const axisY = (viewBox.y ?? 0) + (viewBox.height ?? 0)
  const topGap = stacked
    ? ANNOTATION_CHIP_TOP_GAP_STACKED
    : ANNOTATION_CHIP_TOP_GAP

  return (
    <foreignObject
      x={x - ANNOTATION_CHIP_SIZE / 2}
      y={axisY + topGap}
      width={ANNOTATION_CHIP_SIZE}
      height={ANNOTATION_CHIP_SIZE}
      data-testid="chart-annotation-flag"
      style={{ overflow: 'visible' }}
    >
      <Popover open={open} onOpenChange={onOpenChange}>
        <Tooltip content="View details" open={hovered && !open}>
          <Popover.Trigger>
            <IconButton
              type="button"
              variant="secondary"
              size="small"
              label={ariaLabel}
              className={styles.annotationFlag}
              onPointerEnter={() => setHovered(true)}
              onPointerLeave={() => setHovered(false)}
            >
              <FlagIcon />
            </IconButton>
          </Popover.Trigger>
        </Tooltip>
        <Popover.Content
          // Open below the flag: the flag sits at the foot of the chart (under
          // the x-axis), so opening upward would cover the plotted lines. Radix
          // still auto-flips back up near the viewport edge.
          side="bottom"
          align="center"
          arrow
          // Don't dismiss when focus moves out: closing one flag returns focus to
          // its own trigger, which would otherwise dismiss a sibling flag the user
          // just opened. Focus restoration on close stays default, so keyboard Esc
          // still returns focus here.
          onFocusOutside={(event) => event.preventDefault()}
        >
          {popoverContent}
        </Popover.Content>
      </Popover>
    </foreignObject>
  )
}
